/* eslint-disable react/jsx-fragments */
import React, { useState, useEffect, Fragment } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { promisify } from 'app/actions';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Row, Col } from 'react-bootstrap';
import { Select, ViewModeSelector, Loader } from 'sa-ui';
import * as actions from './reportApplicationActions';

import messages from './reportApplicationMessages';
import ReportApplicationCard from './ReportApplicationCard';
import ReportApplicationUpdateCriteriaModal from './ReportApplicationUpdateCriteriaModal';
import GradePerQuestionTable from '../GradePerQuestionTable';
import YearTabs from '../../app/common/YearTabs';
import * as helpers from './reportApplicationHelpers';

import './ReportApplicationContainer.scss';

const EditState = {
  PRISTINE: 0,
  PENDING: 1,
  UPDATED: 2,
  ERROR: 3,
};

const ReportApplicationContainer = (props) => {
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({});
  const [criteriaState, setCriteriaState] = useState(EditState.PRISTINE);
  const [evaluationSelected, setEvaluationSelected] = useState(null);
  const [filter, setFilter] = useState({});
  const [limit, setLimit] = useState(6);
  const [year, setYear] = useState(new Date().getFullYear());
  const [page, setPage] = useState(1);

  const contractOptions = helpers.getContractOptions(props.groups);
  const schoolOptions = helpers.getSchoolOptions(props.groups, filter.idContract);
  const classesOptions = helpers.getClassOptions(props.groups, filter.idCSchool);
  const evaluationsFiltered = helpers.filterEvaluations(props.evaluations, filter, year);
  const evaluationsPaginated = helpers.paginate(evaluationsFiltered, page, limit);

  async function selectEvaluations(idContract, idCSchool, idESCourse) {
    setFilter({ ...filter, idContract });
    setLoading(true);
    try {
      await props.getEvaluations({
        idContract,
        idCSchool,
        idESCourse,
        checkReport: true,
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleGetGroups() {
    setLoading(true);
    try {
      await props.getGroups({ reportFilter: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    handleGetGroups();
  }, []);

  useEffect(() => {
    if (filter.idContract || filter.idCSchool || filter.idESCourse) {
      selectEvaluations(filter.idContract, filter.idCSchool, filter.idESCourse);
    }
  }, [filter.idContract, filter.idCSchool, filter.idESCourse]);

  useEffect(() => {
    setFilter({
      ...filter,
      idContract: props.contracts.length === 1 ? props.contracts[0].idContract : filter.idContract,
    });
  }, [props.contracts]);

  useEffect(() => {
    if (props.currentYear !== year && props.years.length === 1) {
      setYear(props.currentYear);
    }
  }, [props.currentYear, year]);

  function goBack() {
    props.history.goBack();
  }

  async function handleFinishCriteria(criteria) {
    setLoading(true);
    try {
      await props.updateEvaluationCriteria({
        idEvaluation: evaluationSelected.id,
        criteria,
      });
      setModal({ ...modal, criteria: false });
      setCriteriaState(EditState.PRISTINE);
    } catch (err) {
      setCriteriaState(EditState.ERROR);
    } finally {
      setLoading(false);
    }
  }

  async function handleChangeContract(event) {
    setLoading(true);
    setFilter({
      ...filter,
      idContract: event.target.value,
      idCSchool: undefined,
      idESCourse: undefined,
    });
    await props.getEvaluations({ idContract: event.target.value, checkReport: true });
    setLoading(false);
  }

  function handleChangeSchool(event) {
    setFilter({ ...filter, idCSchool: event.target.value, idESCourse: undefined });
  }

  function handleChangeCourse(event) {
    setFilter({ ...filter, idESCourse: event.target.value });
  }

  function handleCloseCriteriaModal() {
    setCriteriaState(EditState.PRISTINE);
    setModal({ ...modal, criteria: false });
    setEvaluationSelected(null);
  }

  const handleEdit = (evaluation) => async (e) => {
    e.stopPropagation();
    setLoading(true);
    try {
      await props.getEvaluationCriteria({ idEvaluation: evaluation.id });
      setEvaluationSelected(evaluation);
      setModal({ ...modal, criteria: true });
    } finally {
      setLoading(false);
    }
  };

  function handleChangeFilter(value, type) {
    if (type === 'page') {
      setPage(value);
    }

    if (type === 'year') {
      setPage(1);
      setYear(value);
    }
  }

  return (
    <Fragment>
      <Loader show={loading} />
      <ReportApplicationUpdateCriteriaModal
        show={modal.criteria}
        state={criteriaState}
        criteria={props.criteria}
        onFinish={handleFinishCriteria}
        onClose={handleCloseCriteriaModal}
      />
      <Row className="align-items-end report-application-panel">
        <Col md={12}>
          <a className="return" onClick={goBack}>
            <i className="ti-arrow-left" /> {props.intl.formatMessage(messages.back)}
          </a>
          <h5 className="title">{props.intl.formatMessage(messages.title)}</h5>
        </Col>
        <Col md={2}>
          {props.contracts.length === 1 && (
            <div className="contract-info">
              <div className="contract-logo">
                {helpers.getInitials(props.contracts[0].contractName)}
              </div>
              <span className="contract-name">{props.contracts[0].contractName}</span>
            </div>
          )}
          {props.contracts.length > 1 && (
            <Select
              containerClassName="mt-0"
              nullable
              onChange={handleChangeContract}
              options={contractOptions}
              placeholder={props.intl.formatMessage(messages.chain)}
            />
          )}
        </Col>
        <Col md={3}>
          <Select
            containerClassName="mt-0"
            disabled={!filter.idContract}
            nullable
            name="idCSchool"
            onChange={handleChangeSchool}
            value={filter.idCSchool}
            placeholder={props.intl.formatMessage(messages.school)}
            options={schoolOptions}
          />
        </Col>
        <Col md={3}>
          <Select
            containerClassName="mt-0"
            disabled={!filter.idCSchool}
            nullable
            name="idCourse"
            value={filter.idESCourse}
            onChange={handleChangeCourse}
            options={classesOptions}
            placeholder={props.intl.formatMessage(messages.class)}
          />
        </Col>
        {/* <Col>
            <Row className="justify-content-md-end p-0 m-0">
              <ViewModeSelector />
            </Row>
          </Col> */}
      </Row>
      <YearTabs
        year={year}
        years={props.years}
        page={page}
        pages={Math.ceil(
          (evaluationsFiltered.length > 0
            ? evaluationsFiltered.length
            : evaluationsFiltered.length + 1) / limit,
        )}
        onChangePage={(newPage) => handleChangeFilter(newPage, 'page')}
        onChangeYear={(newYear) => handleChangeFilter(newYear, 'year')}
      />
      <Row>
        {!loading &&
          (evaluationsPaginated.length > 0 ? (
            evaluationsPaginated.map((d) => (
              <Col key={d.id} className="d-flex" lg={4}>
                <ReportApplicationCard
                  {...d}
                  coordinator={props.coordinator}
                  style={{ paddingBottom: '15px' }}
                  onEdit={handleEdit(d)}
                  onClick={() =>
                    props.history.push(`/reports/application/${d.id}/assessment-panel`)
                  }
                />
              </Col>
            ))
          ) : (
            <Col className="d-flex">{props.intl.formatMessage(messages.noCardsFound)}</Col>
          ))}
      </Row>
    </Fragment>
  );
};

ReportApplicationContainer.defaultProps = {
  evaluations: [],
  applications: [],
  years: [new Date().getFullYear()],
};

const mapStateToProps = ({ reportApplication, app }, { match }) => ({
  ...reportApplication,
  ...match.params,
  permission: app.permission,
});

export default compose(
  injectIntl,
  withRouter,
  connect(mapStateToProps, promisify(actions)),
)(ReportApplicationContainer);
